import React, { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Edit2, Copy, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { ComplianceSavedFilter } from './types';

interface SavedFiltersDropdownProps {
  savedFilters: ComplianceSavedFilter[];
  selectedFilterId: string | null;
  onSelectFilter: (filterId: string | null) => void;
  onEditFilter: (filter: ComplianceSavedFilter) => void;
  onDeleteFilter: (filterId: string) => void;
  onDuplicateFilter: (filter: ComplianceSavedFilter) => void;
}

export function SavedFiltersDropdown({
  savedFilters,
  selectedFilterId,
  onSelectFilter,
  onEditFilter,
  onDeleteFilter,
  onDuplicateFilter,
}: Readonly<SavedFiltersDropdownProps>) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<ComplianceSavedFilter | null>(null);

  const selectedFilter = savedFilters?.find((f) => f.id === selectedFilterId);

  const handleEdit = () => {
    if (selectedFilter) {
      onEditFilter(selectedFilter);
    }
  };

  const handleDuplicate = () => {
    if (selectedFilter) {
      onDuplicateFilter(selectedFilter);
    }
  };

  const handleDelete = () => {
    if (selectedFilter) {
      setFilterToDelete(selectedFilter);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    if (filterToDelete) {
      onDeleteFilter(filterToDelete.id);
      setFilterToDelete(null);
    }
    setDeleteDialogOpen(false);
  };

  return (
    <>
      <div className="flex gap-2">
        <Select
          value={selectedFilterId ?? 'none'}
          onValueChange={(value: string) => onSelectFilter(value === 'none' ? null : value)}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Select a saved filter..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">
                {!savedFilters || savedFilters.length === 0 ? 'No saved filters yet' : 'Select a saved filter...'}
              </span>
            </SelectItem>
            {savedFilters?.map((filter) => (
              <SelectItem key={filter.id} value={filter.id}>
                {filter.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedFilterId && savedFilters && savedFilters.length > 0 && (
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={handleEdit}
              title="Edit filter"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleDuplicate}
              title="Duplicate filter"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleDelete}
              title="Delete filter"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Filter</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{filterToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
